/**
 * Browser Cache Clearing Utilities
 *
 * Comprehensive utilities for clearing all browser storage mechanisms
 * across Chrome, Firefox, Safari, Edge, and Opera.
 *
 * Supported Storage Types:
 * - IndexedDB
 * - Cache API
 * - Service Workers
 * - Browser History State
 * - HTTP Cache (via hard reload)
 */

/**
 * Detect current browser and version
 *
 * Browser Compatibility: All browsers
 *
 * @returns {{name: string, version: string, engine: string, userAgent: string}}
 *
 * @example
 * const browser = detectBrowser();
 * console.log(`Running on ${browser.name} ${browser.version}`);
 */
export const detectBrowser = () => {
  const userAgent = navigator.userAgent;
  const vendor = navigator.vendor || '';

  let browserName = 'Unknown';
  let browserVersion = 'Unknown';
  let engine = 'Unknown';

  // Chrome (must check before Safari as Chrome also has Safari in UA)
  if (/Chrome/.test(userAgent) && /Google Inc/.test(vendor)) {
    browserName = 'Chrome';
    const match = userAgent.match(/Chrome\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
    engine = 'Blink';
  }
  // Edge (Chromium-based)
  else if (/Edg/.test(userAgent)) {
    browserName = 'Edge';
    const match = userAgent.match(/Edg\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
    engine = 'Blink';
  }
  // Firefox
  else if (/Firefox/.test(userAgent)) {
    browserName = 'Firefox';
    const match = userAgent.match(/Firefox\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
    engine = 'Gecko';
  }
  // Safari (must check after Chrome/Edge)
  else if (/Safari/.test(userAgent) && /Apple Computer/.test(vendor)) {
    browserName = 'Safari';
    const match = userAgent.match(/Version\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
    engine = 'WebKit';
  }
  // Opera
  else if (/OPR/.test(userAgent) || /Opera/.test(userAgent)) {
    browserName = 'Opera';
    const match = userAgent.match(/(?:OPR|Opera)\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
    engine = 'Blink';
  }

  return {
    name: browserName,
    version: browserVersion,
    engine: engine,
    userAgent: userAgent
  };
};

/**
 * Delete a single IndexedDB database
 *
 * @param {string} dbName - Database name to delete
 * @returns {Promise<void>}
 * @private
 */
const deleteDatabase = (dbName) => {
  return new Promise((resolve, reject) => {
    try {
      const deleteRequest = window.indexedDB.deleteDatabase(dbName);

      deleteRequest.onsuccess = () => {
        console.log(`IndexedDB database '${dbName}' deleted successfully`);
        resolve();
      };

      deleteRequest.onerror = () => {
        console.warn(`Error deleting IndexedDB database '${dbName}':`, deleteRequest.error);
        reject(deleteRequest.error);
      };

      deleteRequest.onblocked = () => {
        console.warn(`Deletion of IndexedDB database '${dbName}' blocked. Forcing timeout...`);
        // Try to force close connections by waiting
        setTimeout(() => {
          resolve(); // Resolve anyway after timeout
        }, 1000);
      };
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Clear all IndexedDB databases
 *
 * This function enumerates and deletes all IndexedDB databases in the browser.
 * Handles cross-browser compatibility with fallback for browsers that don't
 * support indexedDB.databases() API (Firefox, older browsers).
 *
 * Browser Compatibility:
 * - Chrome 71+: Full support with databases() API
 * - Firefox: Uses fallback to known database list
 * - Safari 10+: Full support
 * - Edge 79+: Full support (Chromium-based)
 * - Opera 58+: Full support
 *
 * Performance:
 * - Async operation, typically 100-500ms depending on database size
 * - Handles blocked databases with 1s timeout
 *
 * @param {Object} options - Configuration options
 * @param {string[]} options.excludeDatabases - Array of database names to preserve (default: [])
 *
 * @returns {Promise<{success: boolean, cleared: string[], errors: Array<Object>}>}
 * Result object containing:
 *   - success: true if all operations succeeded
 *   - cleared: array of successfully deleted database names
 *   - errors: array of error objects with operation details
 *
 * @example
 * // Clear all databases
 * const result = await clearIndexedDB();
 * console.log(`Cleared ${result.cleared.length} databases`);
 * if (!result.success) {
 *   console.warn('Errors:', result.errors);
 * }
 *
 * @example
 * // Preserve specific databases
 * const result = await clearIndexedDB({
 *   excludeDatabases: ['my-important-db']
 * });
 */
export const clearIndexedDB = async (options = {}) => {
  const { excludeDatabases = [] } = options;

  const result = {
    success: true,
    cleared: [],
    errors: []
  };

  try {
    // Check browser support
    if (!window.indexedDB) {
      result.success = false;
      result.errors.push({
        operation: 'IndexedDB check',
        error: 'IndexedDB not supported in this browser'
      });
      return result;
    }

    // Get all databases
    let databases = [];

    if (window.indexedDB.databases) {
      // Modern API (Chrome 71+, Edge 79+, Opera 58+, Safari 14+)
      try {
        databases = await window.indexedDB.databases();
      } catch (error) {
        console.warn('indexedDB.databases() failed, trying fallback:', error);
        // Fallback: use known database names
        databases = [
          { name: 'localforage' },
          { name: 'redux-persist' },
          // Add other known database names if your app uses them
        ];
      }
    } else {
      // Fallback for Firefox and older browsers
      // These are common database names used by web apps
      databases = [
        { name: 'localforage' },
        { name: 'redux-persist' },
        { name: 'keyval-store' },
        { name: 'workbox-background-sync' },
        { name: 'workbox-expiration' },
      ];
    }

    // Delete each database
    for (const db of databases) {
      const dbName = db.name;

      if (excludeDatabases.includes(dbName)) {
        continue;
      }

      try {
        await deleteDatabase(dbName);
        result.cleared.push(dbName);
      } catch (error) {
        result.success = false;
        result.errors.push({
          operation: 'deleteDatabase',
          database: dbName,
          error: error.message || String(error)
        });
      }
    }

    if (result.cleared.length > 0) {
      console.log(`Cleared ${result.cleared.length} IndexedDB database(s):`, result.cleared);
    }
  } catch (error) {
    result.success = false;
    result.errors.push({
      operation: 'clearIndexedDB',
      error: error.message || String(error)
    });
  }

  return result;
};

/**
 * Clear all Cache API storage
 *
 * Deletes all caches created via the Cache API (typically used by Service Workers
 * and Progressive Web Apps for offline storage).
 *
 * Browser Compatibility:
 * - Chrome 40+: Full support
 * - Firefox 39+: Full support
 * - Safari 11.1+: Full support
 * - Edge 79+: Full support (Chromium-based)
 * - Opera 27+: Full support
 *
 * Performance:
 * - Async operation, typically 50-200ms
 *
 * @returns {Promise<{success: boolean, cleared: string[], errors: Array<Object>}>}
 * Result object containing:
 *   - success: true if all operations succeeded
 *   - cleared: array of successfully deleted cache names
 *   - errors: array of error objects with operation details
 *
 * @example
 * const result = await clearCacheAPI();
 * console.log(`Cleared ${result.cleared.length} caches`);
 */
export const clearCacheAPI = async () => {
  const result = {
    success: true,
    cleared: [],
    errors: []
  };

  try {
    // Check browser support
    if (!('caches' in window)) {
      result.success = false;
      result.errors.push({
        operation: 'Cache API check',
        error: 'Cache API not supported in this browser'
      });
      return result;
    }

    // Get all cache names
    const cacheNames = await caches.keys();

    if (cacheNames.length === 0) {
      return result; // No caches to clear
    }

    // Delete each cache
    for (const cacheName of cacheNames) {
      try {
        const deleted = await caches.delete(cacheName);
        if (deleted) {
          result.cleared.push(cacheName);
        } else {
          result.errors.push({
            operation: 'deleteCache',
            cache: cacheName,
            error: 'Cache deletion returned false (cache may not exist)'
          });
        }
      } catch (error) {
        result.success = false;
        result.errors.push({
          operation: 'deleteCache',
          cache: cacheName,
          error: error.message || String(error)
        });
      }
    }

    if (result.cleared.length > 0) {
      console.log(`Cleared ${result.cleared.length} Cache API storage(s):`, result.cleared);
    }
  } catch (error) {
    result.success = false;
    result.errors.push({
      operation: 'clearCacheAPI',
      error: error.message || String(error)
    });
  }

  return result;
};

/**
 * Clear all Service Worker caches and unregister workers
 *
 * First clears all caches (same as clearCacheAPI), then unregisters all
 * Service Workers. This is the most complete clearing for PWAs.
 *
 * Browser Compatibility:
 * - Chrome 40+: Full support
 * - Firefox 44+: Full support
 * - Safari 11.1+: Full support
 * - Edge 79+: Full support (Chromium-based)
 * - Opera 27+: Full support
 *
 * Performance:
 * - Async operation, typically 100-300ms
 *
 * @returns {Promise<{success: boolean, unregistered: string[], errors: Array<Object>}>}
 * Result object containing:
 *   - success: true if all operations succeeded
 *   - unregistered: array of unregistered Service Worker scopes
 *   - errors: array of error objects with operation details
 *
 * @example
 * const result = await clearServiceWorkers();
 * console.log(`Unregistered ${result.unregistered.length} Service Workers`);
 */
export const clearServiceWorkers = async () => {
  const result = {
    success: true,
    unregistered: [],
    errors: []
  };

  try {
    // Check browser support
    if (!('serviceWorker' in navigator)) {
      result.success = false;
      result.errors.push({
        operation: 'Service Worker check',
        error: 'Service Workers not supported in this browser'
      });
      return result;
    }

    // First, clear all caches (Service Workers use Cache API)
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        if (cacheNames.length > 0) {
          console.log(`Cleared ${cacheNames.length} Service Worker cache(s)`);
        }
      }
    } catch (error) {
      result.errors.push({
        operation: 'clear Service Worker caches',
        error: error.message || String(error)
      });
    }

    // Then, unregister all service workers
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();

      if (registrations.length === 0) {
        return result; // No workers to unregister
      }

      for (const registration of registrations) {
        try {
          const success = await registration.unregister();
          if (success) {
            result.unregistered.push(registration.scope);
          } else {
            result.errors.push({
              operation: 'unregister',
              scope: registration.scope,
              error: 'Unregister returned false (may already be unregistered)'
            });
          }
        } catch (error) {
          result.success = false;
          result.errors.push({
            operation: 'unregister',
            scope: registration.scope,
            error: error.message || String(error)
          });
        }
      }

      if (result.unregistered.length > 0) {
        console.log(`Unregistered ${result.unregistered.length} Service Worker(s):`, result.unregistered);
      }
    } catch (error) {
      result.success = false;
      result.errors.push({
        operation: 'getRegistrations',
        error: error.message || String(error)
      });
    }
  } catch (error) {
    result.success = false;
    result.errors.push({
      operation: 'clearServiceWorkers',
      error: error.message || String(error)
    });
  }

  return result;
};

/**
 * Clear browser history state
 *
 * Resets the window.history.state object which may contain sensitive data
 * from navigation. Uses replaceState to clear without affecting history length.
 *
 * Browser Compatibility: All browsers
 *
 * Performance: Synchronous, instant
 *
 * @returns {void}
 *
 * @example
 * clearHistoryState();
 */
export const clearHistoryState = () => {
  try {
    if (window.history && window.history.replaceState) {
      // Replace current state with null to clear any stored data
      window.history.replaceState(null, '', window.location.href);
      console.log('Browser history state cleared');
    }
  } catch (error) {
    console.warn('Error clearing history state:', error);
  }
};

/**
 * Perform hard reload to clear HTTP cache
 *
 * Forces browser to reload the page and clear HTTP cache by adding a cache-busting
 * parameter to the URL. This ensures all resources are fetched fresh from the server.
 *
 * Browser-Specific Implementation:
 * - Safari: Uses window.location.href with cache bust parameter (Safari doesn't support location.reload(true))
 * - Other browsers: Uses location.href with cache bust parameter (modern approach)
 * - Fallback: location.reload() if URL modification fails
 *
 * Browser Compatibility: All browsers
 *
 * Performance: Triggers full page reload
 *
 * @returns {void}
 *
 * @example
 * // Perform hard reload after clearing storage
 * performHardReload();
 */
export const performHardReload = () => {
  try {
    const browser = detectBrowser();

    // Get current URL
    const currentUrl = window.location.href;

    // Create URL object for manipulation
    let url;
    try {
      url = new URL(currentUrl);
    } catch (error) {
      // Fallback if URL parsing fails
      console.warn('URL parsing failed, using direct reload:', error);
      window.location.reload();
      return;
    }

    // Add cache-busting parameter
    url.searchParams.set('_refresh', Date.now().toString());

    // Remove any existing hash (optional, but cleaner)
    // Uncomment if you want to remove hash on reload
    // url.hash = '';

    // Navigate to new URL with cache bust parameter
    // This works on all browsers and forces a cache refresh
    window.location.href = url.toString();

    console.log(`Performing hard reload on ${browser.name}`);
  } catch (error) {
    console.error('Error performing hard reload:', error);
    // Ultimate fallback
    try {
      window.location.reload();
    } catch (fallbackError) {
      console.error('Fallback reload also failed:', fallbackError);
    }
  }
};

/**
 * Check if browser supports IndexedDB
 *
 * @returns {boolean}
 */
export const supportsIndexedDB = () => {
  return 'indexedDB' in window && window.indexedDB !== null;
};

/**
 * Check if browser supports Cache API
 *
 * @returns {boolean}
 */
export const supportsCacheAPI = () => {
  return 'caches' in window;
};

/**
 * Check if browser supports Service Workers
 *
 * @returns {boolean}
 */
export const supportsServiceWorkers = () => {
  return 'serviceWorker' in navigator;
};
